/* eslint-disable sonarjs/no-collapsible-if */
/* eslint-disable no-irregular-whitespace */
/* eslint-disable max-lines-per-function */
import { toNDigits } from '@mail/cross-sizes-utils';
import { Icon20HelpOutline } from '@vkontakte/icons';
import { Button, ButtonGroup, Spacing } from '@vkontakte/vkui';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { overquotaBlockDate } from 'reactApp/appHelpers/featuresHelpers/features/overquotaBlockDate';
import {
    overquotaBannerConfig,
    overquotaCommunicationPayPlacement,
    overquotaFoldersConfig,
    overquotaNewCommunication,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import { useBuyClickHandlers } from 'reactApp/hooks/useBuyClickHandlers';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { InformationAlertBanner, InformationBannerType } from 'reactApp/ui/InformationAlertBanner/InformationAlertBanner';
import { OverquotaFolders } from 'reactApp/ui/OverquotaFolders/OverquotaFolders';
import { OverquotaBannerCtrl, sendDwhOverquota } from 'reactApp/ui/OverquotaModal/OverquotaModal.helpers';
import { formatFileHistoryDate } from 'reactApp/utils/formatDate';
import opener from 'reactApp/utils/opener';
import { ECategoryGa } from 'reactApp/utils/paymentGa';
import { getUtmString } from 'reactApp/utils/urlHelper';

import styles from './OverquotaBanner.css';

const SOURCE = 'cloud';
const MEDIUM = 'web_union_quota_landing';

const UTM = {
    default: {
        buy: getUtmString({
            utm_source: 'cloud_web',
            utm_medium: 'infoblock',
            utm_campaign: 'overquota_infoblock_new',
        }),
        clear: getUtmString({
            utm_source: 'cloud_web',
            utm_medium: 'infoblock',
            utm_campaign: 'overquota_infoblock_new',
        }),
    },
    step_1: {
        buy: getUtmString({
            utm_source: SOURCE,
            utm_medium: MEDIUM,
            utm_campaign: 'overquota_future_block_infoblock',
        }),
        clear: getUtmString({
            utm_source: SOURCE,
            utm_medium: MEDIUM,
            utm_campaign: 'overquota_future_block_infoblock_cleaner',
        }),
    },
    step_2: {
        buy: getUtmString({
            utm_source: SOURCE,
            utm_medium: MEDIUM,
            utm_campaign: 'overquota_future_delete_infoblock',
        }),
        clear: getUtmString({
            utm_source: SOURCE,
            utm_medium: MEDIUM,
            utm_campaign: 'overquota_future_delete_infoblock_cleaner',
        }),
    },
};

const radarName = 'overquota_splash_new';

export const OverquotaBanner = memo(function OverquotaPopup(): ReactElement | null {
    const storage = useSelector(getCurrentStorage);
    const dispatch = useDispatch();
    const overquota = useSelector(UserQuotaSelectors.getOverQuota);
    const {
        trashSize = 0,
        blockTime = undefined,
        clearTime,
        state,
    }: ReturnType<typeof getUserOverquotaState> = useSelector(getUserOverquotaState);

    const hasTrash = trashSize > 0;

    useEffect(() => {
        sendDwhOverquota({
            action: 'show',
            nameAction: 'overquota_splash_new',
            storage,
            name: radarName,
            category: ECategoryGa.informationAlert,
        });
    }, [storage]);

    const title = useMemo(() => {
        if (overquotaFoldersConfig.enabled) {
            return 'Загружать, смотреть, отправлять файлы пока нельзя, но есть решение';
        }

        if (overquotaBlockDate && blockTime && state === 'started') {
            if (overquotaNewCommunication) {
                return (
                    <>
                        <span>{formatFileHistoryDate(blockTime)}</span> доступ к файлам будет ограничен, но есть решение
                    </>
                );
            }
            return (
                <>
                    <span>{formatFileHistoryDate(blockTime)}</span> доступ к файлам будет заблокирован
                </>
            );
        }

        if (overquotaBlockDate && clearTime && ['blocked', 'unblocked', 'full_blocked'].includes(state || '')) {
            if (overquotaNewCommunication) {
                return (
                    <>
                        <span>{formatFileHistoryDate(clearTime)}</span> файлы будут удалены. Как не допустить
                    </>
                );
            }
            return (
                <>
                    <span>{formatFileHistoryDate(clearTime)}</span> ваши файлы будут удалены
                </>
            );
        }

        return <>Вашим файлам не хватает места</>;
    }, [blockTime, clearTime, state]);

    const content = useMemo(() => {
        if (overquotaFoldersConfig.enabled) {
            return (
                <>
                    Единое пространство Облака и Почты переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской Mail Space.
                    Кроме этого, можно очистить место или папки ниже, они весят больше всего:
                </>
            );
        }

        if (overquotaNewCommunication) {
            if (hasTrash) {
                return (
                    <>
                        Пространство переполнено — увеличьте его с подпиской Mail Space или очистите{' '}
                        <span>
                            {
                                toNDigits({
                                    bytes: trashSize,
                                    maxDigitsCount: 3,
                                }).value
                            }
                        </span>{' '}
                        в корзине
                    </>
                );
            }

            if (overquotaBlockDate && blockTime && state === 'started') {
                return (
                    <>
                        Пространство переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской Mail Space
                    </>
                );
            }

            if (overquotaBlockDate && clearTime && (state === 'blocked' || state === 'unblocked' || state === 'full_blocked')) {
                return (
                    <>
                        Пространство переполнено на <span>{overquota.value}</span> — увеличьте его с подпиской Mail Space, чтобы сохранить
                        файлы
                    </>
                );
            }
        }

        if (overquotaBlockDate && (state === 'started' || state === 'blocked' || state === 'unblocked')) {
            return <>Пространство для ваших файлов переполнено. Увеличьте или очистите место</>;
        }

        return <>Скоро они будут удалены. Увеличьте или очистите место, чтобы их сохранить</>;
    }, [blockTime, clearTime, state, hasTrash, trashSize, overquota.value]);

    const buttonTitle = useMemo(() => {
        if (overquotaNewCommunication) {
            if (overquotaBlockDate && blockTime && state === 'started') {
                return 'Снять ограничения';
            }
        }

        return 'Увеличить место';
    }, [state, blockTime]);

    const searchParams = useMemo(() => {
        let search = UTM.default.clear;

        if (overquotaBlockDate && blockTime && state === 'started') {
            search = UTM.step_1.clear;
        }

        if (overquotaBlockDate && clearTime && (state === 'blocked' || state === 'unblocked')) {
            search = UTM.step_2.clear;
        }
        return search;
    }, [blockTime, clearTime, state]);

    const { showPaywall, goToPromoQuota } = useBuyClickHandlers({
        paySource: overquotaFoldersConfig.enabled ? 'mnz_overquota_banner_struct' : 'mnz_overquota_banner',
        searchParams,
    });

    const secondaryButtonContent = useMemo(() => {
        if (!overquotaNewCommunication) {
            return '';
        }

        if (overquotaBannerConfig.secondaryButton === 'clear') {
            return 'Очистить место';
        }
        if (overquotaBannerConfig.secondaryButton === 'help') {
            return <Icon20HelpOutline />;
        }

        return '';
    }, []);

    const onHelpClickHandler = useCallback(() => {
        sendDwhOverquota({
            action: 'click',
            storage,
            name: radarName,
            category: ECategoryGa.informationAlert,
            control: OverquotaBannerCtrl.help,
        });
        opener(overquotaBannerConfig.helpLink);
    }, []);

    const onClearClickHandler = useCallback(() => {
        sendDwhOverquota({
            action: 'click',
            storage,
            name: radarName,
            category: ECategoryGa.informationAlert,
            control: OverquotaBannerCtrl.clear,
        });

        dispatch(
            historyPush({
                id: '/promo/quota',
                search: `?${searchParams}`,
                hash: 'cleaner',
            })
        );
    }, []);

    if (overquotaFoldersConfig.enabled) {
        return (
            <InformationAlertBanner
                type={InformationBannerType.alert}
                title={<span className={styles.title}>{title}</span>}
                content={
                    <>
                        <div className={styles.content}>{content}</div>
                        <Spacing size={8} />
                        <OverquotaFolders />
                        <Spacing size={12} />
                        <ButtonGroup gap="m" mode="horizontal">
                            <Button
                                size={'l'}
                                appearance={'negative'}
                                mode={'primary'}
                                data-test-id={'overquota_bnr-buy-btn'}
                                data-name="primary-bttn"
                                onClick={overquotaCommunicationPayPlacement === 'paywall' ? showPaywall : goToPromoQuota}
                            >
                                Снять ограничения
                            </Button>
                            {overquotaFoldersConfig.clearButton && (
                                <Button
                                    size={'l'}
                                    appearance={'neutral'}
                                    mode={'primary'}
                                    data-test-id={'overquota_bnr-clear-btn'}
                                    data-name="secondary-bttn"
                                    onClick={onClearClickHandler}
                                >
                                    Очистить место
                                </Button>
                            )}
                        </ButtonGroup>
                    </>
                }
            />
        );
    }

    return (
        <InformationAlertBanner
            type={InformationBannerType.alert}
            title={<span className={styles.title}>{title}</span>}
            content={<div className={styles.content}>{content}</div>}
            primaryButtonTitle={buttonTitle}
            onPrimaryClick={overquotaCommunicationPayPlacement === 'paywall' ? showPaywall : goToPromoQuota}
            secondaryButtonTitle={secondaryButtonContent}
            secondaryButtonClassName={styles.secondaryButton}
            onSecondaryClick={overquotaBannerConfig.secondaryButton === 'help' ? onHelpClickHandler : onClearClickHandler}
        />
    );
});
